import {useContext, useEffect, useMemo, useState} from "react";
import {Link, useParams} from "react-router-dom";
import Data from "../Data";
import AppContext from "../../../context";

const BundleBook = ({ basePath = '' }) => {
    const context = useContext(AppContext);
    const { id } = useParams();
    if(!id) window.location.href = "/";

    const [experience, setExperience] = useState(null);
    const [bundles, setBundles] = useState([]);
    const [bundleId, setBundleId] = useState("");
    const [tickets, setTickets] = useState(1);

    useEffect(async () => {
        try {
            const experience = await Data.fetchExperienceById({ id });
            setExperience(experience.data);
            const bundles = await Data.fetchBundlesForExperience({ experienceId: id });
            setBundles(bundles.data);
            if(bundles.data?.length){
                const urlParams = new URLSearchParams(window.location.search);
                const preselect = urlParams.get('bundle');
                setBundleId(preselect && bundles.data.find(b => b._id === preselect) ? preselect : bundles.data[0]._id);
            }
        } catch(error){
            window.location.href = "/";
        }
    }, []);

    const selectedBundle = useMemo(() => bundles.find(b => b._id === bundleId), [bundles, bundleId]);

    const minTicketsRemaining = useMemo(() => {
        if(!selectedBundle) return 0;
        if(!experience?.futureEvents) return 0;
        // We don't have bundle-level remaining on FE. Use min of ticketsRemaining across the bundle's events from the loaded experience.futureEvents
        const remaining = selectedBundle.events.map(ev => {
            const fromExperience = experience.futureEvents.find(e => e._id === ev._id);
            return fromExperience?.ticketsRemaining ?? 0;
        });
        return Math.max(0, Math.min(...remaining));
    }, [selectedBundle, experience]);

    const savings = useMemo(() => {
        if(!selectedBundle) return 0;
        // Base sum = num events * experience.price
        const base = selectedBundle.events.length * (experience.price || 0);
        return Math.max(0, base - selectedBundle.price);
    }, [selectedBundle, experience]);

    if(!experience) return null;

    return (
        <div className={"p-8"}>
            <div className={"container mx-auto grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                <div className={"grid grid-cols-1 gap-4"}>
                    <div className={"border border-borderGrey rounded-md p-4"}>
                        <p className={"font-semibold"}>Choose a bundle</p>
                        <select value={bundleId} onChange={e => setBundleId(e.target.value)} className={"mt-2 w-full"}>
                            {bundles.map(b => (
                                <option key={b._id} value={b._id}>{b.name} — £{b.price} per person</option>
                            ))}
                        </select>
                        {selectedBundle ? (
                            <div className={"mt-4 text-sm text-textGrey"}>
                                <p>Includes {selectedBundle.events.length} events:</p>
                                <ul className={"list-disc ml-5 mt-2"}>
                                    {selectedBundle.events.map(ev => (
                                        <li key={ev._id}>{new Date(ev.date).toLocaleDateString()} {new Date(ev.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className={"flex flex-col gap-4"}>
                    {selectedBundle ? (
                        <>
                            <div>
                                <p className={"mb-2"}>Tickets</p>
                                <select value={tickets} onChange={e => setTickets(Number(e.target.value))} className={"w-full"}>
                                    {[...Array(minTicketsRemaining).keys()].map(i => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1} ticket{i + 1 > 1 ? 's' : ''}
                                        </option>
                                    ))}
                                </select>
                                {minTicketsRemaining === 0 ? <p className={"text-sm text-red-600 mt-2"}>This bundle is sold out.</p> : null}
                            </div>

                            <div className={"border border-borderGrey rounded-md p-4"}>
                                <p className={"font-semibold mb-2"}>Summary</p>
                                <p>Standard price: £{(experience.price || 0) * selectedBundle.events.length} per person</p>
                                <p>Bundle price: £{selectedBundle.price} per person</p>
                                <p className={"text-green-700"}>You save £{savings} per person</p>
                                <p className={"mt-2 font-semibold"}>Total: £{(selectedBundle.price * tickets).toFixed(2)}</p>
                            </div>

                            <div>
                                <Link to={`${basePath}/${id}/checkout/${context.user ? 'bundle-confirm' : 'create-account'}?bundle=${bundleId}&tickets=${tickets}`}
                                      className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md border-0"}>Continue</Link>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

export default BundleBook

